// Learning Backlog: React UI on the BoldPanelLayout template. The maths lives in
// ./model.js. Each question opens in Learning Value to compare methods and comes back;
// the chosen questions go to Sprint Mix as Learn items (src/lib/handBack.js).

import { useMemo, useState } from "react";
import { BoldPanelLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  DataTable,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  MetricCard,
  MetricGrid,
  OpenInTool,
  Panel,
  RangeInput,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
  type Range,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { hubLink, hubState, initialBack, linkWithBack } from "@/lib/handBack.js";
import { METHODS, methodById } from "@/learning/methods.js";
import { learningShare } from "@/learning/share.js";
import { MAX_QUESTIONS, analyseBacklog, fmt, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { learnBacklogShare } from "./share.js";
import { DEFAULT_STATE as MIX_DEFAULT, mixItem, sprintMixShare } from "@/sprintmix/share.js";

const HEADING = headingFor("tool/learning-backlog");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Question {
  label: string;
  value: Range;
  preset: string;
  noise: number;
  bias: number;
  days: number;
}
interface State {
  unit: string;
  dayValue: number;
  budget: number;
  questions: Question[];
}

const INITIAL = EXAMPLES[0].state as State;
const numberOr = (text: string, fallback: number) => (text.trim() === "" || !Number.isFinite(Number(text)) ? fallback : Number(text));
/** The methods Learning Value opens with for a question: its own first, then three common ones. */
const methodsFor = (q: Question) => {
  const ids = [q.preset, ...["interviews", "fake-door", "prototype"].filter((id) => id !== q.preset)];
  return ids.map((id, k) => {
    const p = methodById(id);
    return k === 0 ? { label: p.label, preset: id, noise: q.noise, bias: q.bias, days: q.days } : { label: p.label, preset: id, noise: p.noise, bias: p.bias, days: 3 };
  });
};

export function App() {
  const shared = useUrlRestore<State>(learnBacklogShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  const [back] = useState(() => initialBack(["sprintmix"]));
  useUrlSync(learnBacklogShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const setQ = (i: number, patch: Partial<Question>) => setState((s) => ({ ...s, questions: s.questions.map((q, k) => (k === i ? { ...q, ...patch } : q)) }));
  const r: Any = useMemo(() => analyseBacklog(state), [state]);
  const u = state.unit;

  const hub: Any = hubState(back, sprintMixShare);
  const mixHref = (() => {
    if (!r.chosen.length) return undefined;
    const base = hub ?? { ...MIX_DEFAULT, capacityUnit: "days", valueUnit: u, dayValue: state.dayValue };
    if (base.capacityUnit !== "days") return undefined;
    const round = (x: number) => Math.round(x * 100) / 100;
    const items = r.chosen.map((q: Any) =>
      mixItem({ label: q.label, kind: "learn", team: q.team, value: { low: round(q.evsi), high: round(q.evsi) }, size: { low: q.days, high: q.days } }),
    );
    return hubLink("/tool/sprint-planner", sprintMixShare, { ...base, items: [...base.items, ...items].slice(-40) });
  })();

  return (
    <BoldPanelLayout
      label="Unit 40 / Learning backlog"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="Experiment prioritization"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      footer={
        <BarFooter>
          <span>Value of information per day · greedy budget fill</span>
          <span>
            {r.chosen.length} chosen · {fmt(r.daysUsed)} of {state.budget} days
          </span>
        </BarFooter>
      }
    >
      <Explainer id="learnbacklog" title="What this answers" summary="Which questions to research first" defaultOpen>
        <p>
          A team in discovery has more open questions than days. This page ranks them by how much learning about
          each could <strong>improve the decision</strong>, less what the research costs, per day of effort — and
          fills the days you have.
        </p>
        <p>
          A question whose answer cannot change what you do is worth nothing to research, however interesting. The
          page says which ones to decide now.
        </p>
      </Explainer>

      <Panel step={1} title="Start from an example" hint="Or list your own questions in 03">
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: invented questions and values.</p>
      </Panel>

      <Panel step={2} title="Budget" hint="Days of research this cycle">
        <FieldGrid>
          <Field label="Research days available">
            <Input type="number" min={0} step="any" aria-label="Research days available" value={String(state.budget)} onChange={(e) => set({ budget: Math.max(0, numberOr(e.target.value, 0)) })} />
          </Field>
          <Field label="Value unit">
            <Input aria-label="Value unit" maxLength={20} value={u} onChange={(e) => set({ unit: e.target.value })} />
          </Field>
          <Field label="Value of one team-day" hint={`In ${u}`}>
            <Input type="number" min={0} step="any" aria-label="Value of one team-day" value={String(state.dayValue)} onChange={(e) => set({ dayValue: Math.max(0, numberOr(e.target.value, 0)) })} />
          </Field>
        </FieldGrid>
      </Panel>

      <Panel step={3} title="Open questions" hint="Value if built, as a 90% range">
        <DataTable<Question>
          rows={state.questions}
          rowKey={(_, i) => i}
          rowClassName={(_, i) => (r.excluded.some((e: Any) => e.index === i) ? "bg-destructive/10" : undefined)}
          columns={[
            { head: "Question", cell: (q, i) => <Input aria-label={`Question ${i + 1}`} className="h-8 min-w-[12rem] rounded-none" value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} /> },
            { head: `Net value if built (${u})`, cell: (q, i) => <RangeInput label={`Question ${i + 1} value`} value={q.value} onChange={(value) => setQ(i, { value })} /> },
            {
              head: "Way to learn",
              cell: (q, i) => (
                <Select
                  aria-label={`Question ${i + 1} way to learn`}
                  value={q.preset}
                  onChange={(e) => {
                    const p = methodById(e.target.value);
                    setQ(i, { preset: p.id, noise: p.noise, bias: p.bias });
                  }}
                >
                  {METHODS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              ),
            },
            { head: "Days", numeric: true, cell: (q, i) => <Input type="number" min={0} step="any" aria-label={`Question ${i + 1} days`} className="h-8 w-20 rounded-none font-mono text-xs" value={String(q.days)} onChange={(e) => setQ(i, { days: Math.max(0, numberOr(e.target.value, 0)) })} /> },
            {
              head: <span className="sr-only">Compare methods</span>,
              cell: (q, i) => (
                <a
                  className="whitespace-nowrap text-sm"
                  aria-label={`Compare ways to learn about question ${i + 1}`}
                  href={linkWithBack("/tool/learning-value", learnBacklogShare, state, {
                    codec: learningShare,
                    state: { decision: q.label, unit: u, dayValue: state.dayValue, value: { ...q.value }, methods: methodsFor(q) },
                  })}
                >
                  Compare →
                </a>
              ),
            },
            {
              head: <span className="sr-only">Remove</span>,
              cell: (_q, i) => (
                <Button variant="outline" size="sm" aria-label={`Remove question ${i + 1}`} onClick={() => setState((s) => ({ ...s, questions: s.questions.filter((_, k) => k !== i) }))}>
                  ×
                </Button>
              ),
            },
          ]}
        />
        <Button
          className="mt-3"
          disabled={state.questions.length >= MAX_QUESTIONS}
          onClick={() => {
            const p = methodById("interviews");
            setState((s) => ({ ...s, questions: [...s.questions, { label: `Question ${s.questions.length + 1}`, value: { low: -10, high: 20 }, preset: p.id, noise: p.noise, bias: p.bias, days: 3 }] }));
          }}
        >
          + Add question
        </Button>
        {r.excluded.length > 0 && (
          <Callout kind="warn" className="mt-4">
            <strong>Not ranked:</strong>
            <ul className="mb-0 mt-1 pl-5">
              {r.excluded.map((e: Any) => (
                <li key={e.index}>
                  {e.label || `Question ${e.index + 1}`}: {e.reasons.join(" ")}
                </li>
              ))}
            </ul>
          </Callout>
        )}
      </Panel>

      <Panel step={4} title="What to learn first" hint="Conditional on each range and method">
        <MetricGrid className="mb-6 lg:grid-cols-3">
          <MetricCard accent eyebrow="Chosen" value={`${r.chosen.length} of ${r.rows.length}`} data-value={r.chosen.length}>
            Questions worth their research time, best value per day first.
          </MetricCard>
          <MetricCard eyebrow="Days used" value={`${fmt(r.daysUsed)} of ${state.budget}`}>
            A question that does not fit is skipped; the next one may.
          </MetricCard>
          <MetricCard eyebrow="Net value of the plan" value={`${fmt(r.netValue)} ${u}`}>
            Expected improvement in decisions, less the cost of the days.
          </MetricCard>
        </MetricGrid>
        <DataTable<Any>
          caption="Ranked by net value per day"
          rows={r.rows}
          rowKey={(q) => q.index}
          columns={[
            { head: "Rank", numeric: true, cell: (q) => String(q.rank).padStart(2, "0") },
            { head: "Question", cell: (q) => q.label },
            { head: "Way to learn", cell: (q) => q.method },
            { head: `Gain (${u})`, numeric: true, cell: (q) => fmt(q.evsi) },
            { head: `Net (${u})`, numeric: true, cell: (q) => fmt(q.net) },
            { head: "Per day", numeric: true, cell: (q) => fmt(q.perDay) },
            {
              head: "Call",
              cell: (q) =>
                q.selected ? <b>Learn first</b> : q.net <= 0 ? <span className="text-muted-foreground">{q.settled ? "Clear: " : ""}{q.decideNow} it now</span> : <span className="text-muted-foreground">Over budget</span>,
            },
          ]}
        />
        <BriefLines className="mt-6" lines={summaryLines(r, state)} />
        <OpenInTool
          title="Put it in the sprint"
          targets={[
            {
              id: "sprintmix",
              name: "Sprint Mix",
              use: hub
                ? "Return to your sprint plan with the chosen questions added as Learn items, valued at their expected gain."
                : "Plan a sprint with the chosen questions as Learn items beside features and fixes, valued at their expected gain.",
              href: mixHref,
              reason: !r.chosen.length ? "No question is chosen yet." : "Your sprint plan sizes work in points; research is in days. Switch the plan to days first.",
            },
          ]}
          note="Each chosen question's name, expected gain, days and team travel."
        />
      </Panel>
    </BoldPanelLayout>
  );
}
